<template>
  <section class="package-list-v2">
    <b-row>
      <b-col cols="12">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div>
            <h4 class="mb-0">Packages</h4>
            <small class="text-muted">Manage ISP packages, pricing, and status</small>
          </div>
          <div class="d-flex align-items-center">
            <b-button variant="primary" :to="{ name: 'isp-package-create' }">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Add Package
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-card no-body class="mb-2 card-statistics">
      <b-card-header>
        <b-card-title>Package Overview</b-card-title>
        <b-card-text class="font-small-2 mb-0">Quick KPIs for the selected ISP</b-card-text>
      </b-card-header>
      <b-card-body class="statistics-body">
        <b-row>
          <b-col
            v-for="item in statisticsItems"
            :key="item.label"
            xl="3"
            sm="6"
            class="mb-1"
          >
            <b-media no-body>
              <b-media-aside class="mr-2">
                <b-avatar size="48" :variant="item.color">
                  <feather-icon :icon="item.icon" size="24" />
                </b-avatar>
              </b-media-aside>
              <b-media-body class="my-auto">
                <h4 class="font-weight-bolder mb-0">{{ item.value }}</h4>
                <b-card-text class="font-small-3 mb-0">
                  {{ item.label }}
                </b-card-text>
              </b-media-body>
            </b-media>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body class="mb-2">
      <b-card-header>
        <b-card-title>Mapping Health</b-card-title>
        <b-card-text class="font-small-2 mb-0">Coverage check for profile mapping</b-card-text>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col md="3" sm="6" class="mb-1">
            <div class="d-flex align-items-center">
              <b-avatar size="40" variant="light-success" class="mr-1">
                <feather-icon icon="CheckCircleIcon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ mappingHealth.mapped }}</div>
                <small class="text-muted">Mapped</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-1">
            <div class="d-flex align-items-center">
              <b-avatar size="40" variant="light-warning" class="mr-1">
                <feather-icon icon="AlertTriangleIcon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ mappingHealth.partial }}</div>
                <small class="text-muted">Partial</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-1">
            <div class="d-flex align-items-center">
              <b-avatar size="40" variant="light-danger" class="mr-1">
                <feather-icon icon="XCircleIcon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ mappingHealth.unmapped }}</div>
                <small class="text-muted">Unmapped</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-1">
            <div class="d-flex align-items-center">
              <b-avatar size="40" variant="light-primary" class="mr-1">
                <feather-icon icon="CpuIcon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ mappingHealth.routerCount }}</div>
                <small class="text-muted">Routers</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body class="mb-2">
      <b-card-body>
        <b-row class="align-items-end">
          <b-col md="4" lg="3">
            <b-form-group label="Search">
              <b-form-input
                v-model="searchTerm"
                placeholder="Search by name, bandwidth, validity"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="3">
            <b-form-group label="Status">
              <b-form-select v-model="filters.status" :options="statusOptions" />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="3">
            <b-form-group label="Bandwidth">
              <b-form-select v-model="filters.bandwidth" :options="bandwidthOptions" />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="3">
            <b-form-group label="Validity">
              <b-form-select v-model="filters.validity" :options="validityOptions" />
            </b-form-group>
          </b-col>
          <b-col md="4" lg="3">
            <b-form-group label="Mapping Status">
              <b-form-select v-model="filters.mapping" :options="mappingStatusOptions" />
            </b-form-group>
          </b-col>
          <b-col md="6" lg="4">
            <b-form-group label="Profile Mapping Mode">
              <div class="d-flex align-items-center">
                <b-form-select
                  v-model="mappingModeDraft"
                  :options="mappingModeOptions"
                  class="mr-1"
                  :disabled="mappingModeLoading || mappingModeSaving"
                />
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="mappingModeSaving || mappingModeDraft === mappingMode"
                  @click="saveMappingMode"
                >
                  Update
                </b-button>
              </div>
              <small class="text-muted">Current: {{ mappingModeLabel }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <small class="text-muted">
                Showing {{ filteredRows.length }} packages
              </small>
              <div class="d-flex align-items-center">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  @click="resetFilters"
                >
                  Reset Filters
                </b-button>
                <div class="d-flex align-items-center">
                  <span class="text-nowrap mr-50">Rows</span>
                  <b-form-select
                    v-model="pageLength"
                    :options="pageOptions"
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <div v-if="isLoading" class="text-center text-muted my-2">
          <b-spinner small class="mr-50" />
          Loading packages...
        </div>
        <b-alert v-else-if="loadError" variant="danger" show>
          {{ loadError }}
        </b-alert>
        <vue-good-table
          v-else
          :columns="columns"
          :rows="filteredRows"
          :rtl="direction"
          :line-numbers="true"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
          }"
        >
          <template slot="table-row" slot-scope="props">
            <div v-if="props.column.field === 'package_name'" class="text-nowrap">
              <span class="font-weight-bolder text-dark">{{ props.row.package_name }}</span>
            </div>

            <div v-else-if="props.column.field === 'bandwidth'" class="text-nowrap">
              <b-badge variant="light-primary">
                {{ formatBandwidth(props.row.bandwidth) }}
              </b-badge>
            </div>

            <div v-else-if="props.column.field === 'validity'" class="text-nowrap">
              <b-badge variant="light-info">
                {{ formatValidity(props.row.validity) }}
              </b-badge>
            </div>

            <div v-else-if="props.column.field === 'price'" class="text-nowrap">
              <span class="font-weight-bold text-dark">{{ formatPriceWithCurrency(props.row.price) }}</span>
            </div>

            <span v-else-if="props.column.field === 'status'">
              <b-badge :variant="statusVariant(props.row.status)">
                {{ props.row.status }}
              </b-badge>
            </span>

            <span v-else-if="props.column.field === 'mapping'">
              <b-badge :variant="mappingBadgeVariant(props.row.id)">
                {{ mappingBadgeLabel(props.row.id) }}
              </b-badge>
            </span>

            <span v-else-if="props.column.field === 'action'">
              <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item :to="'/isp/package-edit/' + props.row.id">
                  <feather-icon icon="Edit2Icon" class="mr-50" />
                  <span>Edit</span>
                </b-dropdown-item>
                <b-dropdown-item @click="openMappingModal(props.row)">
                  <feather-icon icon="LinkIcon" class="mr-50" />
                  <span>Profile Mapping</span>
                </b-dropdown-item>
              </b-dropdown>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap"> Showing 1 to </span>
                <b-form-select
                  v-model="pageLength"
                  :options="pageOptions"
                  class="mx-1"
                  size="sm"
                  @input="(value) => props.perPageChanged({ currentPerPage: value })"
                />
                <span class="text-nowrap"> of {{ props.total }} entries </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => props.pageChanged({ currentPage: value })"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>

        <div v-if="!isLoading && !loadError && filteredRows.length === 0" class="text-center text-muted my-2">
          No packages found
        </div>

        <b-modal
          v-model="showMappingModal"
          :title="mappingPackage ? `Profile Mapping - ${mappingPackage.package_name}` : 'Profile Mapping'"
          @hidden="resetMappingModal"
          hide-footer
          size="lg"
        >
          <div v-if="mappingLoading" class="text-center text-muted my-2">
            <b-spinner small class="mr-50" />
            Loading mapping...
          </div>
          <div v-else>
            <b-alert variant="info" show>
              Mapping mode: <strong>{{ mappingModeLabel }}</strong>
            </b-alert>

            <div v-if="mappingMode === 'global'">
              <b-form-group label="Profile Source Router">
                <b-form-select
                  v-model="mappingRouterId"
                  :options="routerOptions"
                  text-field="label"
                  value-field="value"
                  :disabled="!routerOptions.length"
                  @change="handleGlobalRouterChange"
                />
              </b-form-group>

              <b-form-group label="Profile Name">
                <b-form-select
                  v-if="globalProfileOptions.length"
                  v-model="mappingProfileName"
                  :options="globalProfileOptions"
                />
                <b-form-input
                  v-else
                  v-model="mappingProfileName"
                  placeholder="Enter profile name"
                />
              </b-form-group>
              <small class="text-muted d-block mb-1">This profile will be used for all routers.</small>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="clearGlobalMapping"
              >
                Clear Mapping
              </b-button>
            </div>

            <div v-else>
              <b-alert v-if="!routerOptions.length" variant="warning" show>
                No routers found for this ISP.
              </b-alert>
              <b-table
                v-else
                :items="routerTableItems"
                :fields="routerMappingFields"
                responsive
                small
              >
                <template #cell(router)="data">
                  <div class="font-weight-bolder text-dark text-nowrap">{{ data.item.name }}</div>
                  <small class="text-muted">Router ID: {{ data.item.id }}</small>
                </template>
                <template #cell(profile)="data">
                  <div class="d-flex align-items-center">
                    <b-form-select
                      v-if="profileOptionsForRouter(data.item.id).length"
                      v-model="routerProfileMap[data.item.id]"
                      :options="profileOptionsForRouter(data.item.id)"
                      text-field="text"
                      value-field="value"
                      size="sm"
                    />
                    <b-form-input
                      v-else
                      v-model="routerProfileMap[data.item.id]"
                      placeholder="Enter profile name"
                      size="sm"
                    />
                    <b-spinner
                      v-if="profilesLoading[data.item.id]"
                      small
                      class="ml-50"
                    />
                    <b-button
                      v-else-if="!profileOptionsForRouter(data.item.id).length"
                      size="sm"
                      variant="outline-primary"
                      class="ml-50"
                      @click="loadProfiles(data.item.id, true)"
                    >
                      Load
                    </b-button>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="clearRouterMapping(data.item.id)"
                  >
                    Clear
                  </b-button>
                </template>
              </b-table>
              <b-alert
                v-if="modalMissingRouters.length"
                variant="warning"
                show
                class="mt-1"
              >
                <div class="font-weight-bold mb-50">Missing Routers</div>
                <div class="d-flex flex-wrap">
                  <b-badge
                    v-for="router in modalMissingRouters"
                    :key="router.value"
                    variant="light-warning"
                    class="mr-50 mb-50"
                  >
                    {{ router.label }}
                  </b-badge>
                </div>
              </b-alert>
            </div>

            <div class="d-flex justify-content-end mt-2">
              <b-button
                variant="outline-secondary"
                class="mr-1"
                @click="showMappingModal = false"
              >
                Cancel
              </b-button>
              <b-button
                variant="primary"
                :disabled="mappingSaving"
                @click="saveMapping"
              >
                Save Mapping
              </b-button>
            </div>
          </div>
        </b-modal>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardTitle,
  BCardText,
  BCardBody,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BBadge,
  BButton,
  BDropdown,
  BDropdownItem,
  BAvatar,
  BMedia,
  BMediaAside,
  BMediaBody,
  BPagination,
  BAlert,
  BSpinner,
  BModal,
  BTable,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import store from "@/store";
import {
  ISP_ROUTER_LIST,
  PACKAGE_LIST,
  PACKAGE_PROFILE_MAPPING_MODE,
  PACKAGE_PROFILE_MAPS,
  PACKAGE_PROFILE_MAPS_DELETE,
} from "@core/services/api.js";
import { checkRes } from "@core/services/helper";
import Mixin from "@core/services/mixin";
import $ from "jquery";

export default {
  mixins: [Mixin],
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BBadge,
    BButton,
    BDropdown,
    BDropdownItem,
    BAvatar,
    BMedia,
    BMediaAside,
    BMediaBody,
    BPagination,
    BAlert,
    BSpinner,
    BModal,
    BTable,
    VueGoodTable,
  },
  data() {
    return {
      pageLength: 100,
      pageOptions: [100, 300, 500, 1000],
      dir: false,
      columns: [
        {
          label: "Package Name",
          field: "package_name",
        },
        {
          label: "Bandwidth",
          field: "bandwidth",
        },
        {
          label: "Validity",
          field: "validity",
        },
        {
          label: "Price",
          field: "price",
        },
        {
          label: "Status",
          field: "status",
        },
        {
          label: "Profile Mapping",
          field: "mapping",
        },
        {
          label: "Action",
          field: "action",
        },
      ],
      rows: [],
      searchTerm: "",
  filters: {
    status: null,
    bandwidth: null,
    validity: null,
    mapping: null,
  },
      isp_id: store.state.appConfig.isp_id,
      isLoading: false,
      loadError: "",
      mappingMode: "global",
      mappingModeDraft: "global",
      mappingModeLoading: false,
      mappingModeSaving: false,
      mappingSummary: {},
      routerOptions: [],
      showMappingModal: false,
      mappingPackage: null,
      mappingProfileName: "",
      mappingRouterId: null,
      routerProfileMap: {},
      existingRouterProfileMap: {},
      profilesByRouter: {},
      profilesLoading: {},
      mappingLoading: false,
      mappingSaving: false,
      routerMappingFields: [
        { key: "router", label: "Router" },
        { key: "profile", label: "Profile" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    statisticsItems() {
      const total = this.rows.length;
      const active = this.rows.filter(row => row.status === "Active").length;
      const inactive = total - active;
      const avgPrice = this.calculateAveragePrice(this.rows);

      return [
        {
          label: "Total Packages",
          value: total,
          icon: "BoxIcon",
          color: "light-primary",
        },
        {
          label: "Active",
          value: active,
          icon: "CheckCircleIcon",
          color: "light-success",
        },
        {
          label: "Inactive",
          value: inactive,
          icon: "XCircleIcon",
          color: "light-secondary",
        },
        {
          label: "Avg Price",
          value: this.formatPrice(avgPrice),
          icon: "TrendingUpIcon",
          color: "light-info",
        },
      ];
    },
    statusOptions() {
      return [
        { value: null, text: "All Status" },
        { value: "Active", text: "Active" },
        { value: "Inactive", text: "Inactive" },
      ];
    },
    mappingStatusOptions() {
      return [
        { value: null, text: "All Mapping" },
        { value: "mapped", text: "Mapped" },
        { value: "partial", text: "Partial" },
        { value: "unmapped", text: "Unmapped" },
      ];
    },
    mappingModeOptions() {
      return [
        { value: "global", text: "Global" },
        { value: "router", text: "Router-wise" },
      ];
    },
    mappingModeLabel() {
      return this.mappingMode === "router" ? "Router-wise" : "Global";
    },
    globalProfileOptions() {
      if (!this.mappingRouterId) return [];
      const profiles = this.profilesByRouter[this.mappingRouterId] || [];
      return profiles.map(profile => ({ value: profile, text: profile }));
    },
    routerTableItems() {
      return this.routerOptions.map(router => ({
        id: router.value,
        name: router.label,
      }));
    },
    bandwidthOptions() {
      const values = this.rows.map(row => row.bandwidth).filter(Boolean);
      const unique = [...new Set(values)];
      return [{ value: null, text: "All Bandwidth" }].concat(
        unique.map(value => ({ value, text: value })),
      );
    },
    validityOptions() {
      const values = this.rows.map(row => row.validity).filter(Boolean);
      const unique = [...new Set(values)];
      return [{ value: null, text: "All Validity" }].concat(
        unique.map(value => ({ value, text: value })),
      );
    },
    modalMissingRouters() {
      if (!this.mappingPackage) return [];
      return this.mappingMissingRouters(this.mappingPackage.id);
    },
    filteredRows() {
      return this.rows.filter(row => {
        const matchesStatus = this.filters.status ? row.status === this.filters.status : true;
        const matchesBandwidth = this.filters.bandwidth ? row.bandwidth === this.filters.bandwidth : true;
        const matchesValidity = this.filters.validity ? row.validity === this.filters.validity : true;
        const status = this.mappingStatusForPackage(row.id);
        const matchesMapping = this.filters.mapping ? status === this.filters.mapping : true;
        return matchesStatus && matchesBandwidth && matchesValidity && matchesMapping;
      });
    },
    mappingHealth() {
      const totalPackages = this.rows.length;
      const routerCount = this.routerOptions.length;
      let mapped = 0;
      let partial = 0;
      let unmapped = 0;

      this.rows.forEach(pkg => {
        const status = this.mappingStatusForPackage(pkg.id);
        if (status === "mapped") mapped += 1;
        else if (status === "partial") partial += 1;
        else if (status === "unmapped") unmapped += 1;
      });

      if (this.mappingMode === "global") {
        partial = 0;
      }

      return {
        totalPackages,
        mapped,
        partial,
        unmapped,
        routerCount,
      };
    },
    statusVariant() {
      return status => {
        if (status === "Active") return "light-success";
        if (status === "Inactive") return "light-secondary";
        return "light-warning";
      };
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        this.dir = true;
        return this.dir;
      }
      this.dir = false;
      return this.dir;
    },
  },
  mounted() {
    const selected = $("#ispListId option:selected");
    const selectedValue = selected.length ? this.normalizeIspId(selected.val()) : null;
    if (selectedValue) {
      this.isp_id = selectedValue;
    } else if (!this.isp_id) {
      const storedIspId = this.normalizeIspId(JSON.parse(localStorage.getItem("userData"))?.isp_id);
      if (storedIspId) {
        this.isp_id = storedIspId;
      }
    }
    const instance = this;
    $("#ispListId").change(function () {
      const value = instance.normalizeIspId($(this).children("option:selected").val());
      instance.isp_id = value;
      instance.packageList(instance.isp_id);
      instance.fetchRouters(instance.isp_id);
      instance.fetchMappingMode(instance.isp_id);
      instance.fetchMappingSummary(instance.isp_id);
    });
    this.packageList(this.isp_id);
    this.fetchRouters(this.isp_id);
    this.fetchMappingMode(this.isp_id);
    this.fetchMappingSummary(this.isp_id);
  },
  methods: {
    normalizeIspId(value) {
      if (value === undefined || value === null) return null;
      const normalized = String(value).trim();
      if (!normalized || normalized === "null" || normalized === "undefined") return null;
      return normalized;
    },
    fetchRouters(ispId) {
      const baseUrl = process.env.VUE_APP_BASEURL + ISP_ROUTER_LIST;
      const resolvedIspId = this.normalizeIspId(ispId);
      const url = resolvedIspId ? `${baseUrl}?isp_id=${resolvedIspId}` : baseUrl;
      this.$http
        .get(url)
        .then(res => {
          const payload = res.data && res.data.data ? res.data.data : [];
          let items = [];
          if (Array.isArray(payload)) {
            items = payload;
          } else if (payload && Array.isArray(payload.data)) {
            items = payload.data;
          } else if (payload && typeof payload === "object") {
            items = Object.values(payload);
          }

          this.routerOptions = items
            .filter(Boolean)
            .map(item => ({
              value: item.value !== undefined ? item.value : item.id,
              label: item.label || item.router_name || item.name || item.text,
            }))
            .filter(item => item.value !== undefined && item.label);
        })
        .catch(() => {
          this.routerOptions = [];
        });
    },
    fetchMappingMode(ispId) {
      this.mappingModeLoading = true;
      const baseUrl = process.env.VUE_APP_BASEURL + PACKAGE_PROFILE_MAPPING_MODE;
      const resolvedIspId = this.normalizeIspId(ispId);
      const url = resolvedIspId ? `${baseUrl}?isp_id=${resolvedIspId}` : baseUrl;
      this.$http
        .get(url)
        .then(res => {
          const mode = res.data && res.data.data && res.data.data.profile_mapping_mode
            ? res.data.data.profile_mapping_mode
            : "global";
          this.mappingMode = mode;
          this.mappingModeDraft = mode;
        })
        .catch(() => {
          this.mappingMode = "global";
          this.mappingModeDraft = "global";
        })
        .finally(() => {
          this.mappingModeLoading = false;
        });
    },
    saveMappingMode() {
      this.mappingModeSaving = true;
      const resolvedIspId = this.normalizeIspId(this.isp_id);
      const payload = {
        profile_mapping_mode: this.mappingModeDraft,
      };
      if (resolvedIspId) {
        payload.isp_id = resolvedIspId;
      }
      this.$http
        .post(process.env.VUE_APP_BASEURL + PACKAGE_PROFILE_MAPPING_MODE, payload)
        .then(res => {
          if (checkRes(res.data.code)) {
            this.mappingMode = this.mappingModeDraft;
            this.toastMessage("success", "Package Mapping", res);
            this.fetchMappingSummary(this.isp_id);
          } else {
            this.toastMessage("warning", "Package Mapping", res);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.mappingModeSaving = false;
        });
    },
    fetchMappingSummary(ispId) {
      const baseUrl = process.env.VUE_APP_BASEURL + PACKAGE_PROFILE_MAPS;
      const resolvedIspId = this.normalizeIspId(ispId);
      const url = resolvedIspId ? `${baseUrl}?isp_id=${resolvedIspId}` : baseUrl;
      this.$http
        .get(url)
        .then(res => {
          const maps = res.data.data || [];
          const summary = {};
          if (Array.isArray(maps)) {
            maps.forEach(map => {
              if (!summary[map.package_id]) {
                summary[map.package_id] = { count: 0, profile_name: null, router_ids: [] };
              }
              if (map.router_id) {
                const routerId = String(map.router_id);
                if (!summary[map.package_id].router_ids.includes(routerId)) {
                  summary[map.package_id].router_ids.push(routerId);
                  summary[map.package_id].count += 1;
                }
              } else {
                summary[map.package_id].profile_name = map.profile_name;
              }
            });
          }
          this.mappingSummary = summary;
        })
        .catch(() => {
          this.mappingSummary = {};
        });
    },
    mappingBadgeLabel(packageId) {
      const status = this.mappingStatusForPackage(packageId);
      if (this.mappingMode === "global") {
        return status === "mapped" ? "Mapped" : "Unmapped";
      }
      const total = this.routerOptions.length;
      if (!total) return "No Routers";
      const entry = this.mappingSummary[packageId];
      const count = entry ? entry.count : 0;
      if (status === "mapped") return "Mapped";
      if (status === "unmapped") return "Unmapped";
      const missingCount = this.mappingMissingRouters(packageId).length;
      return `Missing ${missingCount}`;
    },
    mappingBadgeVariant(packageId) {
      const status = this.mappingStatusForPackage(packageId);
      if (status === "mapped") return "light-success";
      if (status === "partial") return "light-warning";
      if (status === "unmapped") return "light-danger";
      return "light-secondary";
    },
    mappingMissingRouters(packageId) {
      if (this.mappingMode !== "router") return [];
      const entry = this.mappingSummary[packageId];
      const mappedIds = entry && Array.isArray(entry.router_ids)
        ? entry.router_ids.map(String)
        : [];

      return this.routerOptions.filter(router => {
        const routerId = String(router.value);
        return !mappedIds.includes(routerId);
      });
    },
    openMappingModal(packageRow) {
      this.mappingPackage = packageRow;
      this.showMappingModal = true;
      this.mappingProfileName = "";
      this.mappingRouterId = this.routerOptions.length ? this.routerOptions[0].value : null;
      this.routerProfileMap = {};
      this.existingRouterProfileMap = {};
      if (this.mappingMode === "router") {
        this.routerOptions.forEach(router => {
          this.$set(this.routerProfileMap, router.value, "");
        });
        this.routerOptions.forEach(router => {
          this.loadProfiles(router.value);
        });
      } else if (this.mappingRouterId) {
        this.loadProfiles(this.mappingRouterId);
      }
      this.loadMappingForPackage(packageRow.id);
    },
    resetMappingModal() {
      this.mappingPackage = null;
      this.mappingProfileName = "";
      this.mappingRouterId = null;
      this.routerProfileMap = {};
      this.existingRouterProfileMap = {};
      this.mappingLoading = false;
      this.mappingSaving = false;
    },
    loadMappingForPackage(packageId) {
      if (!packageId) return;
      this.mappingLoading = true;
      const url = `${process.env.VUE_APP_BASEURL + PACKAGE_PROFILE_MAPS}?isp_id=${this.isp_id}&package_id=${packageId}`;
      this.$http
        .get(url)
        .then(res => {
          const maps = res.data.data || [];
          if (this.mappingMode === "global") {
            const globalMap = Array.isArray(maps)
              ? maps.find(map => !map.router_id)
              : null;
            this.mappingProfileName = globalMap ? globalMap.profile_name : "";
          } else if (Array.isArray(maps)) {
            const mapByRouter = {};
            maps.forEach(map => {
              if (map.router_id) {
                mapByRouter[map.router_id] = map.profile_name;
              }
            });
            this.existingRouterProfileMap = { ...mapByRouter };
            this.routerOptions.forEach(router => {
              const profile = mapByRouter[router.value] || "";
              this.$set(this.routerProfileMap, router.value, profile);
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          this.mappingLoading = false;
        });
    },
    handleGlobalRouterChange() {
      if (this.mappingRouterId) {
        this.loadProfiles(this.mappingRouterId, true);
      }
    },
    profileOptionsForRouter(routerId) {
      const profiles = this.profilesByRouter[routerId] || [];
      return profiles.map(profile => ({ value: profile, text: profile }));
    },
    loadProfiles(routerId, force = false) {
      if (!routerId) return Promise.resolve([]);
      if (!force && this.profilesByRouter[routerId]) {
        return Promise.resolve(this.profilesByRouter[routerId]);
      }
      this.$set(this.profilesLoading, routerId, true);
      const url = `${process.env.VUE_APP_BASEURL}/api/get-ppp-profiles/${routerId}`;
      return this.$http
        .get(url)
        .then(res => {
          if (res.data.code === 200) {
            this.$set(this.profilesByRouter, routerId, res.data.data.profiles || []);
          } else {
            this.$set(this.profilesByRouter, routerId, []);
          }
          return this.profilesByRouter[routerId];
        })
        .catch(() => {
          this.$set(this.profilesByRouter, routerId, []);
          return [];
        })
        .finally(() => {
          this.$set(this.profilesLoading, routerId, false);
        });
    },
    saveMapping() {
      if (!this.mappingPackage) return;
      const packageId = this.mappingPackage.id;
      const requests = [];
      const baseUrl = process.env.VUE_APP_BASEURL;
      const ispId = this.normalizeIspId(this.isp_id);

      this.mappingSaving = true;
      if (this.mappingMode === "global") {
        if (this.mappingProfileName) {
          const payload = {
            package_id: packageId,
            profile_name: this.mappingProfileName,
          };
          if (ispId) {
            payload.isp_id = ispId;
          }
          requests.push(this.$http.post(baseUrl + PACKAGE_PROFILE_MAPS, payload));
        } else {
          const payload = {
            package_id: packageId,
          };
          if (ispId) {
            payload.isp_id = ispId;
          }
          requests.push(this.$http.post(baseUrl + PACKAGE_PROFILE_MAPS_DELETE, payload));
        }
      } else {
        const mappings = [];
        this.routerOptions.forEach(router => {
          const profile = this.routerProfileMap[router.value];
          if (profile) {
            mappings.push({
              router_id: router.value,
              profile_name: profile,
            });
          }
        });
        if (mappings.length) {
          const payload = { package_id: packageId, mappings };
          if (ispId) {
            payload.isp_id = ispId;
          }
          requests.push(this.$http.post(baseUrl + PACKAGE_PROFILE_MAPS, payload));
        }
        Object.keys(this.existingRouterProfileMap).forEach(routerId => {
          if (!this.routerProfileMap[routerId]) {
            const payload = { package_id: packageId, router_id: routerId };
            if (ispId) {
              payload.isp_id = ispId;
            }
            requests.push(this.$http.post(baseUrl + PACKAGE_PROFILE_MAPS_DELETE, payload));
          }
        });
      }

      if (!requests.length) {
        this.mappingSaving = false;
        this.showMappingModal = false;
        return;
      }

      Promise.all(requests)
        .then(responses => {
          const first = responses.find(Boolean);
          if (first) {
            if (checkRes(first.data.code)) {
              this.toastMessage("success", "Package Mapping", first);
            } else {
              this.toastMessage("warning", "Package Mapping", first);
            }
          }
          this.fetchMappingSummary(this.isp_id);
        })
        .catch(() => {})
        .finally(() => {
          this.mappingSaving = false;
          this.showMappingModal = false;
        });
    },
    clearGlobalMapping() {
      this.mappingProfileName = "";
    },
    clearRouterMapping(routerId) {
      this.$set(this.routerProfileMap, routerId, "");
    },
    packageList(ispId) {
      this.isLoading = true;
      this.loadError = "";
      const resolvedIspId = this.normalizeIspId(ispId);
      const baseUrl = process.env.VUE_APP_BASEURL + PACKAGE_LIST;
      const url = resolvedIspId ? `${baseUrl}?isp_id=${resolvedIspId}` : baseUrl;
      this.$http
        .get(url)
        .then((res) => {
          const rows = res.data.data || [];
          this.rows = rows.slice().sort((a, b) => {
            const nameA = (a.package_name || "").toString();
            const nameB = (b.package_name || "").toString();
            return nameA.localeCompare(nameB, undefined, { sensitivity: "base", numeric: true });
          });
          this.fetchMappingSummary(resolvedIspId);
        })
        .catch(() => {
          this.loadError = "Failed to load packages. Please try again.";
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    calculateAveragePrice(rows) {
      const amounts = rows
        .map(row => Number(row.price))
        .filter(value => !Number.isNaN(value));
      if (!amounts.length) return 0;
      const total = amounts.reduce((sum, value) => sum + value, 0);
      return total / amounts.length;
    },
    formatPrice(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return value;
      return amount.toFixed(2);
    },
    formatPriceWithCurrency(value) {
      if (value === null || value === undefined || value === "") return value;
      const amount = Number(value);
      if (Number.isNaN(amount)) return value;
      const formatted = amount % 1 === 0
        ? String(Math.trunc(amount))
        : amount.toFixed(2).replace(/\.?0+$/, "");
      return `${formatted} TK`;
    },
    formatBandwidth(value) {
      if (value === null || value === undefined || value === "") return value;
      const text = String(value).trim();
      if (!text) return text;
      if (/mbps/i.test(text)) return text;
      return `${text} Mbps`;
    },
    formatValidity(value) {
      if (value === null || value === undefined || value === "") return value;
      const text = String(value).trim();
      if (!text) return text;
      if (/day/i.test(text)) return text;
      return `${text} Days`;
    },
    resetFilters() {
      this.filters = {
        status: null,
        bandwidth: null,
        validity: null,
        mapping: null,
      };
      this.searchTerm = "";
    },
    mappingStatusForPackage(packageId) {
      if (this.mappingMode === "global") {
        const entry = this.mappingSummary[packageId];
        return entry && entry.profile_name ? "mapped" : "unmapped";
      }
      const total = this.routerOptions.length;
      if (!total) return "unmapped";
      const entry = this.mappingSummary[packageId];
      const count = entry ? entry.count : 0;
      if (count === 0) return "unmapped";
      if (count === total) return "mapped";
      return "partial";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
